import React, { createContext, useContext, useState } from "react"

import { PermissionFeature, PermissionFeatureToActions, Permissions } from "@/protocols/permission"

export type PermissionDataProps = {
	permissions: Permissions
}

type PermissionCheck = {
	feature: PermissionFeature
	action: PermissionFeatureToActions[PermissionFeature]
}

export type PermissionKey =
	| "system:admin"
	| "chat:view-chat-bot-queue" | "chat:view-message-blast-queue" | "chat:view-integrations-queue" | "chat:view-archived-queue" | "chat:view-other-attendant-attendances" | "chat:filter-by-team" | "chat:filter-by-attendant" | "chat:finish-others-attendance" | "chat:transfer-any-chat" | "chat:delete-chat" | "chat:delete-message" | "chat:start-attendance"
	| "role:create" | "role:update" | "role:delete"
	| "attendant:delete" | "attendant:update" | "attendant:invite" | "attendant:block"
	| "instance:update"
	| "manager-panel:access" | "manager-panel:subscriptions-access"
	| "admin-console:access"
	| "profile:update"
	| "contact-note:create" | "contact-note:edit-others" | "contact-note:delete-others"
	| "custom-field:create" | "custom-field:update" | "custom-field:delete"
	| "quick-reply:create" | "quick-reply:update" | "quick-reply:delete" | "quick-reply:import"
	| "channel:edit" | "channel:disconnect" | "channel:connect"
	| "message-blast:create" | "message-blast:update" | "message-blast:delete" | "message-blast:cancel-scheduled" | "message-blast:schedule" | "message-blast:fire"
	| "chat-bot:create" | "chat-bot:update" | "chat-bot:delete" | "chat-bot:duplicate" | "chat-bot:export"
	| "contact:create" | "contact:update" | "contact:disable" | "contact:block-unblock-waba" | "contact:export" | "contact:send-to-bot"
	| "integration:create" | "integration:update" | "integration:delete"
	| "tag:create" | "tag:update" | "tag:delete"
	| "team:create" | "team:update" | "team:delete"
	| "waba-template:create" | "waba-template:update" | "waba-template:delete"
	| "trackable-link:create" | "trackable-link:update"
	| "snooze:manage"
	| "plugin:disconnect" | "plugin:connect"
	| "board-metrics:access" | "board-metrics:edit-config"

export interface PermissionStateContextData {
	permissions: Permissions
	setPermissionData: (data: Partial<PermissionDataProps>) => void
	checkPermission: (feature: PermissionFeature, action: PermissionFeatureToActions[PermissionFeature]) => boolean
	checkPermissions: (checks: PermissionCheck[]) => Record<PermissionKey, boolean>
	checkChatPermission: (action: PermissionFeatureToActions["chat"]) => boolean
	checkChatPermissions: (actions: PermissionFeatureToActions["chat"][]) => Record<PermissionKey, boolean>
}

const checkPermissionAgainst = (
	permissions: Permissions,
	feature: PermissionFeature,
	action: PermissionFeatureToActions[PermissionFeature]
): boolean => {
	const systemPermissions = permissions.system ?? []
	const adminPermission = systemPermissions.find(permission => permission.action === "admin")
	const hasAdminPermission = adminPermission?.effect === "allow"

	if (hasAdminPermission) {
		return true
	}

	const featurePermissions = permissions[feature] ?? []
	const currentPermission = featurePermissions.find(permission => permission.action === action)
	const hasPermission = currentPermission?.effect === "allow"

	return hasPermission
}

const checkPermissionsAgainst = (permissions: Permissions, checks: PermissionCheck[]): Record<PermissionKey, boolean> => {
	const checkToResult = {} as Record<PermissionKey, boolean>

	for (const check of checks) {
		const checkKey = `${check.feature}:${check.action}` as PermissionKey
		checkToResult[checkKey] = checkPermissionAgainst(permissions, check.feature, check.action)
	}

	return checkToResult
}

/**
 * Some dialogs (see 'renderComponent') are mounted on a detached React root instead of
 * inside the app tree, so they have no PermissionStateProvider ancestor and useContext falls
 * back to this default value. Keeping this mirror in sync lets checkPermission keep working
 * (instead of being undefined) for components rendered outside the provider.
 */
let latestPermissions: Permissions = {}

const PermissionStateStore = createContext<PermissionStateContextData>({
	permissions: {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setPermissionData: () => {},
	checkPermission: (feature, action) => checkPermissionAgainst(latestPermissions, feature, action),
	checkPermissions: (checks) => checkPermissionsAgainst(latestPermissions, checks),
	checkChatPermission: (action) => checkPermissionAgainst(latestPermissions, "chat", action),
	checkChatPermissions: (actions) => checkPermissionsAgainst(latestPermissions, actions.map(action => ({ feature: "chat" as const, action })))
})

export const usePermissionStateStore = () => useContext(PermissionStateStore)

const PermissionStateProvider: React.FC = (props) => {
	const { children } = props

	const [permissionData, setPermissionDataState] = useState<PermissionDataProps>({
		permissions: {}
	})

	const setPermissionData = (data: Partial<PermissionDataProps>) => {
		setPermissionDataState(lastState => ({
			...lastState,
			...data
		}))
	}

	const { permissions } = permissionData

	latestPermissions = permissions

	const checkPermission = (feature: PermissionFeature, action: PermissionFeatureToActions[PermissionFeature]): boolean => {
		return checkPermissionAgainst(permissions, feature, action)
	}

	const checkPermissions = (checks: PermissionCheck[]): Record<PermissionKey, boolean> => {
		return checkPermissionsAgainst(permissions, checks)
	}

	const checkChatPermission = (action: PermissionFeatureToActions["chat"]): boolean => {
		return checkPermission("chat", action)
	}

	const checkChatPermissions = (actions: PermissionFeatureToActions["chat"][]): Record<PermissionKey, boolean> => {
		const checks = actions.map(action => ({ feature: "chat" as const, action }))

		return checkPermissions(checks)
	}

	PermissionStateStore.displayName = "PermissionStateStore"

	return (
		<PermissionStateStore.Provider
			value={{
				permissions,
				setPermissionData,
				checkPermission,
				checkPermissions,
				checkChatPermission,
				checkChatPermissions
			}}
		>
			{children}
		</PermissionStateStore.Provider>
	)
}

export default PermissionStateProvider
